// import { useIsFocused } from '@react-navigation/native'
import Color from 'color'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DataTable from 'react-data-table-component'
import { View } from 'react-native'
import { ProgressBar } from 'react-native-paper'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { merge } from 'lodash'
import { composeStyle } from '../helpers'
import { BtText } from './BtText'
import { colors } from '../theme'
import { BtInput } from './BtInput'

/**
 * @param props
 * @param { boolean } props.showOverflow - For Popper to be shown
 * @type {React.NamedExoticComponent<object>}
 */
const ApiTable = function ApiTable(props) {
  const { t } = useTranslation('global')
  const [{ items = [], count = 0 }, setData] = useState({})
  const [search, setSearch] = useState('')
  const [filterParams, setFilterParams] = useState({})
  const defaultSortColumn = useMemo(() => {
    if (props.defaultSortColumn) {
      return props.defaultSortColumn
    }
    const { id, name, selector } =
      props.columns.find((column) => column.sortable !== false) || {}
    return typeof selector === 'string'
      ? selector
      : typeof name === 'string'
      ? name
      : typeof id === 'string'
      ? id
      : '_id'
  }, [props.columns, props.defaultSortColumn])
  const [[sortColumn, sortDirection], setSort] = useState([defaultSortColumn])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const r = useRef(null)
  // added this state to check if initial load is done
  const [isFilterTouched, setIsFilterTouched] = useState(false)

  // const isFocused = useIsFocused()

  const {
    style = {},
    title = '',
    noTableHead = false,
    disabled,
    CustomFilterComponent: Filter,
    filterPlaceholder,
    apiCaller,
    showOverflow,
    expandableRows = false,
    expandableRowsComponent,
    expandableRowDisabled = null,
    expandableRowExpanded,
    extraApiParams = {},
    conditionalRowStyles = [],
    showFilters = true,
    showPagination = true,
    store,
    refresher,
    testID = 'bt_api_table',
    filterWidth = '50%',
    hasFilterTouched, // this prop will return isFilterTouched value to parent component
    highlightOnHover = true,
    ...rest
  } = props

  const callAPI = useCallback(() => {
    if (r && r.current) {
      r.current.cancel()
    }
    setData({
      items: [],
      count: 0,
    })

    setLoading(true)
    setErrorMessage('')
    r.current = apiCaller({
      ...filterParams,
      ...extraApiParams,
      search,
      sortColumn,
      sortDirection,
      page,
      pageSize,
    })

    r.current
      .then((data) => {
        setLoading(false)
        const { items, count, ...rest } = data
        if (store) {
          store.setData({ items, count: count || items.length || 0, ...rest })
        } else {
          setData({
            items: items || [],
            count: count || items?.length || 0,
            ...rest,
          })
        }
      })
      .catch((e) => {
        setLoading(false)
        setErrorMessage(e.data?.message || e.message || e.toString())
      })
  }, [
    apiCaller,
    search,
    extraApiParams,
    filterParams,
    sortColumn,
    sortDirection,
    page,
    pageSize,
  ])

  useEffect(() => {
    // if (!disabled && !loading && isFocused && !props.data) {
    if (!disabled && !loading && !props.data) {
      callAPI()
    }
  }, [
    disabled,
    // isFocused,
    search,
    filterParams,
    sortColumn,
    sortDirection,
    page,
    pageSize,
    refresher,
  ])

  const filters = useMemo(
    () =>
      !!Filter ? (
        <Filter
          value={filterParams}
          onChange={setFilterParams}
          testID={`${testID}_custom_filter_component`}
        />
      ) : null,
    [filterParams, refresher],
  )

  const filterComponent = useMemo(
    () => (
      <FilterComponent
        search={search}
        onSearch={setSearch}
        placeholder={filterPlaceholder}
        filters={filters}
        filterInputTestID={`${testID}_filter_input`}
        filterWidth={filterWidth}
      />
    ),
    [search, filters, setSearch, refresher],
  )

  // this useEffect to only set isFilterTouched value
  useEffect(() => {
    if (search.length > 0) {
      setIsFilterTouched(true)
      hasFilterTouched && hasFilterTouched(true)
    }

    if (filterParams?.status) {
      setIsFilterTouched(true)
      hasFilterTouched && hasFilterTouched(true)
    }

    if (filterParams?.duration) {
      setIsFilterTouched(true)
      hasFilterTouched && hasFilterTouched(true)
    }
  }, [search, filterParams])

  return (
    <DataTable
      {...rest}
      style={composeStyle(style, showOverflow && { overflow: 'visible' })}
      onRowClicked={props.onItemPress}
      noHeader={!title}
      title={title}
      noTableHead={noTableHead}
      noContextMenu
      subHeader={props.searchable}
      subHeaderComponent={showFilters ? filterComponent : null}
      subHeaderAlign="left"
      columns={props.columns}
      data={
        props.items ? props.items.slice() : store ? store.items.slice() : items
      }
      sortServer
      defaultSortField={sortColumn}
      defaultSortAsc={sortDirection !== 'desc'}
      onSort={useCallback((column, sortDirection) => {
        const { id, name, selector } = column
        const sortColumn =
          typeof selector === 'string'
            ? selector
            : typeof name === 'string'
            ? name
            : typeof id === 'string'
            ? id
            : '_id'
        setSort([sortColumn, sortDirection])
      }, [])}
      pagination={showPagination}
      paginationServer
      paginationDefaultPage={page}
      paginationPerPage={pageSize}
      paginationTotalRows={count}
      paginationRowsPerPageOptions={useMemo(() => [25], [])}
      onChangePage={useCallback((page) => setPage(page), [])}
      onChangeRowsPerPage={useCallback((pageSize) => setPageSize(pageSize), [])}
      progressPending={loading}
      progressComponent={useMemo(
        () => (
          <ProgressBar indeterminate visible={loading} testID="progress_bar" />
        ),
        [loading],
      )}
      noDataComponent={useMemo(
        () =>
          errorMessage ? (
            <BtText
              style={{ color: colors.accent }}
              testID={`${testID}_error_text`}
            >
              {errorMessage}
            </BtText>
          ) : props?.noDataComponent ? (
            props?.noDataComponent
          ) : (
            <BtText testID={`${testID}_no_items_text`}>{t('noItems')}</BtText>
          ),
        [errorMessage, isFilterTouched],
      )}
      highlightOnHover={highlightOnHover}
      customStyles={merge(customStyles, props.style)}
      conditionalRowStyles={conditionalRowStyles}
      sortIcon={useMemo(
        () => (
          <View style={{ lineHeight: 16 }}>
            <MaterialCommunityIcons
              name="chevron-down"
              size={20}
              color={colors.placeholder}
            />
          </View>
        ),
        [],
      )}
      keyField={props.keyField}
      expandableRowsHideExpander
      expandOnRowClicked
      expandableRows={expandableRows}
      expandableRowsComponent={expandableRows ? expandableRowsComponent : false}
      expandableRowDisabled={expandableRowDisabled}
      expandableRowExpanded={expandableRowExpanded}
    />
  )
}

ApiTable.propTypes = {
  apiCaller: PropTypes.func.isRequired,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  onItemPress: PropTypes.func,
  CustomFilterComponent: PropTypes.elementType,
  keyField: PropTypes.string,
  showOverflow: PropTypes.bool,
  conditionalRowStyles: PropTypes.arrayOf(PropTypes.object),
  style: PropTypes.object,
  testID: PropTypes.string,
  filterWidth: PropTypes.string,
}

ApiTable.defaultProps = {
  searchable: true,
  disabled: false,
  keyField: 'id',
  testID: 'api_table',
}

export default observer(ApiTable)

const FilterComponent = React.memo(function FilterComponent(props) {
  const [value, setValue] = useState(props.search)
  const placeholder = props.placeholder || 'Business name, account number...'
  const testID = props.filterInputTestID || 'api_table_filter_input'
  const changeTime = useRef(new Date().getTime())
  useEffect(() => {
    changeTime.current = new Date().getTime()
    const id = setTimeout(() => props.onSearch(value), 500)
    return () => clearTimeout(id)
  }, [value, changeTime, props.onSearch])
  useEffect(() => {
    if (changeTime.current < new Date().getTime()) {
      setValue(props.search)
    }
  }, [props.search])
  return (
    <View
      style={composeStyle(
        { width: '100%' },
        props.filters
          ? { flexDirection: 'row', flexWrap: 'wrap' }
          : { alignItems: 'center' },
      )}
    >
      <BtInput
        value={value}
        style={{
          width: props.filterWidth,
          backgroundColor: '#F7F9FC',
          borderWidth: 1,
          borderColor: '#E4E9F2',
        }}
        containerStyle={{ borderWidth: 1, borderColor: '#E4E9F2', height: 40 }}
        onChangeText={setValue}
        placeholder={placeholder}
        rightComponent={
          <MaterialCommunityIcons
            name="magnify"
            size={25}
            color={colors.placeholder}
          />
        }
        testID={testID}
      />
      {props.filters}
    </View>
  )
})

FilterComponent.propTypes = {
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  filters: PropTypes.node,
  filterInputTestID: PropTypes.string,
}

FilterComponent.defaultProps = {}

const customStyles = {
  table: {
    style: { backgroundColor: colors.surface },
  },
  tableWrapper: {
    style: { backgroundColor: colors.surface },
  },
  header: {
    style: { paddingLeft: 0, paddingRight: 0, minHeight: undefined },
  },
  subHeader: {
    style: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 32,
    },
  },
  head: {
    style: {},
  },
  headRow: {
    style: {
      borderBottomColor: Color(colors.borderLight).alpha(0.6).rgb().string(),
      minHeight: 48,
    },
    denseStyle: {},
  },
  headCells: {
    style: {
      color: colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
    },
    activeSortStyle: {
      color: colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
      width: '100%',

      div: {
        width: '100%',
      },
    },
    inactiveSortStyle: {
      color: colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
      width: '100%',

      div: {
        width: '100%',
      },
      span: {
        textDecoration: 'none',
        div: {
          svg: {
            opacity: '0.2 !important',
          },
        },
      },
    },
  },
  contextMenu: {
    style: {},
    activeStyle: {},
  },
  cells: {
    style: {
      color: colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: '400',
    },
  },
  rows: {
    style: {
      borderBottomColor: Color(colors.borderLight).alpha(0.6).rgb().string(),
    },
    selectedHighlightStyle: {},
    denseStyle: {},
    highlightOnHoverStyle: {
      backgroundColor: Color(colors.accentText).alpha(0.08).rgb().string(),
      borderBottomColor: Color(colors.borderLight).alpha(0.6).rgb().string(),
      outlineColor: Color(colors.borderLight).alpha(0.6).rgb().string(),
    },
    stripedStyle: {},
  },
  expanderRow: {
    style: {},
  },
  expanderCell: {
    style: {},
  },
  expanderButton: {
    style: {},
  },
  pagination: {
    style: {},
    pageButtonsStyle: {},
  },
  noData: {
    style: {},
  },
  progress: {
    style: { width: '100%' },
  },
}
